import { ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { format } from '../../../../utils/percentage';
import { PerformanceAnalysisTableModalButton } from '../parts/PerformanceAnalysisTableModalButton';
import { PerformanceAnalysisTableTooltip } from '../parts/PerformanceAnalysisTableTooltip';
import {
  PerformanceAnalysis,
  PerformanceAnalysisDetailRowModalData,
} from '../types';

export function useColumns({
  onShowModal,
  onDownload,
}: {
  onShowModal: (data: PerformanceAnalysisDetailRowModalData) => void;
  onDownload: (data: PerformanceAnalysis) => void;
}): ColumnTable<PerformanceAnalysis>[] {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_PERFORMANCE_ANALYSIS,
  );

  const table = useMemo(() => {
    return t<
      string,
      {
        studyPlanVersionsMany: string;
        teachersMany: string;
        sectionSchedulesActivitiesMany: string;
        headers: {
          sectionCode: string;
          studyPlanVersions: string;
          courseName: string;
          courseCode: string;
          sectionSchedulesActivities: string;
          teachers: string;
          studentsCount: string;
          studentsPassedCount: string;
          studentsFailedCount: string;
          studentsPendingCount: string;
          studentsPassedPercentage: string;
          action: string;
        };
      }
    >('table', {
      returnObjects: true,
    });
  }, [t]);

  return [
    {
      columnName: 'sectionCode',
      headerText: table.headers.sectionCode,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
    },
    {
      columnName: 'studyPlanVersions',
      headerText: table.headers.studyPlanVersions,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return (
          <PerformanceAnalysisTableModalButton
            onClick={() =>
              onShowModal({
                type: 'studyPlanVersions',
                params: row.studyPlanVersions,
              })
            }
            many={table.studyPlanVersionsMany}
            names={row.studyPlanVersions.map((s) => s.name)}
          />
        );
      },
    },
    {
      columnName: 'courseName',
      headerText: table.headers.courseName,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'courseCode',
      headerText: table.headers.courseCode,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'sectionSchedulesActivities',
      headerText: table.headers.sectionSchedulesActivities,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return (
          <PerformanceAnalysisTableTooltip
            names={row.sectionSchedulesActivities.map(
              ({ activityName }) => activityName,
            )}
            many={table.sectionSchedulesActivitiesMany}
          />
        );
      },
    },
    {
      columnName: 'teachers',
      headerText: table.headers.teachers,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        return (
          <PerformanceAnalysisTableModalButton
            onClick={() =>
              onShowModal({
                type: 'teachers',
                params: row.teachers.map((t) => ({
                  name: t.accountFullName,
                  identificationType: t.identificationTypeName,
                  identificationValue: t.identificationValueFormated,
                })),
              })
            }
            many={table.teachersMany}
            names={row.teachers.map((teacher) => (
              <>
                {teacher.accountFullName}
                <br />
                {teacher.identificationTypeName}{' '}
                {teacher.identificationValueFormated}
              </>
            ))}
          />
        );
      },
    },
    {
      columnName: 'studentsCount',
      headerText: table.headers.studentsCount,
      tdClassName: 'text-center',
      thClassName: 'text-center',
      bold: true,
    },
    {
      columnName: 'studentsPassedCount',
      headerText: table.headers.studentsPassedCount,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'studentsFailedCount',
      headerText: table.headers.studentsFailedCount,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'studentsPendingCount',
      headerText: table.headers.studentsPendingCount,
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'studentsPassedPercentage',
      headerText: table.headers.studentsPassedPercentage,
      tdClassName: 'text-center text-primary',
      thClassName: 'text-center',
      bold: true,
      cellFormat: ({ value }) => {
        return format(value);
      },
    },
    {
      columnName: 'action',
      headerText: table.headers.action,
      tdClassName: 'text-center text-primary',
      thClassName: 'text-center',
      width: 120,
      cellFormat: ({ row }) => (
        <>
          <button className="btn btn-icon" onClick={() => onDownload(row)}>
            <Icon name="download" />
          </button>
        </>
      ),
    },
  ];
}
